/**
 * useAIDiscover Hook
 * 
 * React hook for interacting with the AI Discovery API.
 * Handles debouncing, caching, rate limiting, error fallback,
 * session tracking, and the 5-prompt guest authentication gate.
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import API from '../api';
import { useUser } from '../context/UserContext';
import { trackEvent } from '../utils/analytics';

// Constants
const FREE_PROMPT_LIMIT = 5;
const DEFAULT_DEBOUNCE_MS = 300;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_ENTRIES = 30;

// Module-level cache shared across hook instances
const queryCache = new Map();

/**
 * @param {Object} options
 * @param {'canada' | 'global'} options.market - User's current market context
 * @param {number} [options.debounceMs=300] - Debounce delay in ms
 * @returns {Object} Hook state and methods
 */
const useAIDiscover = ({ market = 'global', debounceMs = DEFAULT_DEBOUNCE_MS } = {}) => {
  const { user } = useUser();
  const [results, setResults] = useState(null);
  const [resultsVersion, setResultsVersion] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [isFallback, setIsFallback] = useState(false);

  // Prompt counting for guest authentication gate
  const [promptCount, setPromptCount] = useState(0);

  // Session ID for the current tab
  const sessionIdRef = useRef(null);
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const lastQueryRef = useRef('');

  // Initialize prompt count from sessionStorage (client-side only)
  useEffect(() => {
    if (typeof window !== 'undefined' && !user) {
      const stored = sessionStorage.getItem('ai_discover_prompt_count');
      if (stored) {
        setPromptCount(parseInt(stored, 10) || 0);
      }
    }
  }, [user]);

  // Generate session ID (once per tab)
  useEffect(() => {
    if (typeof window !== 'undefined' && !sessionIdRef.current) {
      const stored = sessionStorage.getItem('ai_discover_session_id');
      if (stored) {
        sessionIdRef.current = stored;
      } else {
        const id = typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : Math.random().toString(36).substring(2) + Date.now().toString(36);
        sessionIdRef.current = id;
        sessionStorage.setItem('ai_discover_session_id', id);
      }
    }
  }, []);

  // When user logs in mid-session, reset prompt counter
  useEffect(() => {
    if (user) {
      setPromptCount(0);
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('ai_discover_prompt_count');
      }
    }
  }, [user]);

  // Derived: does the guest need to log in?
  const requiresLogin = useMemo(
    () => !user && promptCount >= FREE_PROMPT_LIMIT,
    [user, promptCount]
  );

  /**
   * Check the client-side cache for a query result.
   */
  const getCachedResult = useCallback((query, mkt) => {
    const key = `${mkt}:${query.toLowerCase().trim()}`;
    const entry = queryCache.get(key);
    if (entry && Date.now() - entry.timestamp < CACHE_TTL_MS) {
      return entry.data;
    }
    if (entry) {
      queryCache.delete(key); // expired
    }
    return null;
  }, []);

  /**
   * Store a result in the client-side cache.
   */
  const setCachedResult = useCallback((query, mkt, data) => {
    const key = `${mkt}:${query.toLowerCase().trim()}`;
    // Evict oldest if at capacity
    if (queryCache.size >= MAX_CACHE_ENTRIES) {
      const firstKey = queryCache.keys().next().value;
      queryCache.delete(firstKey);
    }
    queryCache.set(key, { data, timestamp: Date.now() });
  }, []);

  /**
   * Core discover function — calls the AI Discovery API.
   * Returns the response data or null on error.
   */
  const discoverImmediate = useCallback(
    async (query) => {
      if (!query || !query.trim()) {
        setResults(null);
        setError(null);
        return null;
      }

      const trimmedQuery = query.trim();
      lastQueryRef.current = trimmedQuery;

      // Auth gate check
      if (!user && promptCount >= FREE_PROMPT_LIMIT) {
        // Don't call API, just signal login required
        return null;
      }

      // Set loading immediately so any listening useEffect knows
      // not to process stale results while we resolve the query
      setIsLoading(true);
      setError(null);
      setIsFallback(false);

      // Check cache first
      const cached = getCachedResult(trimmedQuery, market);
      if (cached) {
        // Spread into a new object so React detects a state change
        // even if the same query was previously cleared and re-requested
        setResults({ ...cached });
        setResultsVersion(v => v + 1);
        setIsLoading(false);
        return cached;
      }
      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      try {
        const payload = {
          query: trimmedQuery,
          market,
          sessionId: sessionIdRef.current,
        };

        // Include userId if logged in
        if (user?.email) {
          payload.userId = user.email;
        }

        const startTime = Date.now();

        const response = await API.post('/api/ai/discover', payload, {
          signal: abortControllerRef.current.signal,
          timeout: 15000,
        });

        const latencyMs = Date.now() - startTime;
        const data = response.data;

        // Normalize results
        const normalized = {
          products: data.products || [],
          brands: data.brands || [],
          collections: data.collections || [],
          suggestions: data.suggestions || [],
          intent: data.intent || {},
          cached: data.cached || false,
        };

        setResults(normalized);
        setResultsVersion(v => v + 1);
        setCachedResult(trimmedQuery, market, normalized);

        // Increment prompt counter for guests (only on non-cached responses)
        if (!user && !data.cached) {
          const newCount = promptCount + 1;
          setPromptCount(newCount);
          if (typeof window !== 'undefined') {
            sessionStorage.setItem('ai_discover_prompt_count', String(newCount));
          }
        }

        // Analytics
        trackEvent('ai_discover_results', {
          query: trimmedQuery,
          market,
          productCount: normalized.products.length,
          brandCount: normalized.brands.length,
          collectionCount: normalized.collections.length,
          cached: normalized.cached,
          latencyMs,
          crossMarketResults: normalized.products.some(
            (p) => (p.country || '').toLowerCase() !== market
          ),
        });

        setIsLoading(false);
        return normalized;
      } catch (err) {
        // Ignore abort errors (from cancelled requests)
        if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
          return null;
        }

        const status = err.response?.status;

        // Rate limited
        if (status === 429) {
          setIsRateLimited(true);
          setError('Too many searches. Please wait a moment and try again.');
          setIsLoading(false);
          trackEvent('ai_discover_fallback', {
            query: trimmedQuery,
            reason: 'rate_limited',
          });
          return null;
        }

        // Feature disabled
        if (status === 503) {
          setIsFallback(true);
          setError(null);
          setIsLoading(false);
          trackEvent('ai_discover_fallback', {
            query: trimmedQuery,
            reason: 'disabled',
          });
          return null;
        }

        // Generic error
        console.error('AI Discovery error:', err);
        setError(
          err.message || 'Something went wrong. Please try again.'
        );
        setIsLoading(false);

        trackEvent('ai_discover_fallback', {
          query: trimmedQuery,
          reason: 'error',
        });

        return null;
      }
    },
    [market, user, promptCount, getCachedResult, setCachedResult]
  );

  /**
   * Debounced discover — use for typeahead / as-you-type.
   */
  const discoverDebounced = useCallback(
    (query) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        discoverImmediate(query);
      }, debounceMs);
    },
    [discoverImmediate, debounceMs]
  );

  /**
   * Primary discover function — immediate (no debounce).
   * Use for form submit / enter key / suggestion chip clicks.
   */
  const discover = useCallback(
    (query) => {
      // Cancel any pending debounce
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      return discoverImmediate(query);
    },
    [discoverImmediate]
  );

  /**
   * Clear results and error state.
   */
  const clearResults = useCallback(() => {
    setResults(null);
    setResultsVersion(v => v + 1);
    setError(null);
    setIsRateLimited(false);
    setIsFallback(false);
    lastQueryRef.current = '';
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    results,
    resultsVersion,
    isLoading,
    error,
    isRateLimited,
    isFallback,
    requiresLogin,
    promptCount,
    discover,
    discoverDebounced,
    clearResults,
    lastQuery: lastQueryRef.current,
    sessionId: sessionIdRef.current,
    FREE_PROMPT_LIMIT,
  };
};

export default useAIDiscover;
